/** React Query hooks for workflow-run CRUD + launch */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '@bastion/data';
import type { WorkflowRun, WorkflowRunLaunchResult, WorkflowRunTagCount } from '../types';

/** Fetch all workflow runs */
export function useWorkflowRuns(filters?: {
  status?: string;
  workflow_name?: string;
  tag?: string;
  limit?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.workflow_name) params.set('workflow_name', filters.workflow_name);
  if (filters?.tag) params.set('tag', filters.tag);
  params.set('limit', String(filters?.limit ?? 200));

  return useQuery({
    queryKey: ['ml', 'workflow-runs', filters],
    queryFn: () => authenticatedFetch<WorkflowRun[]>(`/ml/workflow-runs?${params.toString()}`),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.some((e) => e.status === 'running' || e.status === 'waiting_for_gpu')) return 5_000;
      return 15_000;
    },
  });
}

/** Fetch unique workflow-run tags with usage counts */
export function useWorkflowRunTags() {
  return useQuery({
    queryKey: ['ml', 'workflow-run-tags'],
    queryFn: () => authenticatedFetch<WorkflowRunTagCount[]>('/ml/workflow-run-tags'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch a single workflow run by ID */
export function useWorkflowRun(workflowRunId: string | null) {
  return useQuery({
    queryKey: ['ml', 'workflow-runs', workflowRunId],
    queryFn: () => authenticatedFetch<WorkflowRun>(`/ml/workflow-runs/${workflowRunId}`),
    enabled: !!workflowRunId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'running' || data?.status === 'waiting_for_gpu') return 5_000;
      return false;
    },
  });
}

/** Create a new workflow run */
export function useCreateWorkflowRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      description?: string;
      gpu_instance_type?: string;
      tags?: string[];
    }) =>
      authenticatedFetch<WorkflowRun>('/ml/workflow-runs', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-runs'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-run-tags'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'experiments'] });
    },
  });
}

/** Update a workflow run with partial fields */
export function useUpdateWorkflowRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...updates }: { id: string } & Record<string, unknown>) =>
      authenticatedFetch<{ success: boolean; id: string }>(`/ml/workflow-runs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-runs'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-runs', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-run-tags'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'experiments'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'experiments', variables.id] });
    },
  });
}

/** Launch a workflow run */
export function useLaunchWorkflowRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workflowRunId: string) =>
      authenticatedFetch<WorkflowRunLaunchResult>(`/ml/workflow-runs/${workflowRunId}/launch`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-runs'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'experiments'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['dagster', 'runs'] });
    },
  });
}

/** Delete a workflow run */
export function useDeleteWorkflowRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workflowRunId: string) =>
      authenticatedFetch<{ success: boolean }>(`/ml/workflow-runs/${workflowRunId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-runs'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'workflow-run-tags'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'experiments'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['ml', 'gpu', 'jobs'] });
    },
  });
}

/** @deprecated Use useWorkflowRuns */
export const useExperiments = useWorkflowRuns;
/** @deprecated Use useWorkflowRun */
export const useExperiment = useWorkflowRun;
/** @deprecated Use useCreateWorkflowRun */
export const useCreateExperiment = useCreateWorkflowRun;
/** @deprecated Use useUpdateWorkflowRun */
export const useUpdateExperiment = useUpdateWorkflowRun;
/** @deprecated Use useLaunchWorkflowRun */
export const useLaunchExperiment = useLaunchWorkflowRun;
/** @deprecated Use useDeleteWorkflowRun */
export const useDeleteExperiment = useDeleteWorkflowRun;
